/* eslint-disable tailwindcss/no-custom-classname */

'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useMemo, useRef, useState } from 'react';

import Icon from '@/components/v3/Icon';
import { ImageResponsiveCMSProps } from '@/types/cms';

import Button, { ButtonProps } from '../../base/ui/Button';

interface TabsProps {
  tag: string;
  title: string;
  description: string;
  label: string;
  buttons: ButtonProps[];
  image: ImageResponsiveCMSProps;
}

export interface TabCardsProps {
  id: string;
  title: string;
  description: string;
  tabs: TabsProps[];
}

type TTabsClassNames = {
  containerClassName?: string;
};

export function TabsCards({
  id,
  title,
  description,
  tabs,
  containerClassName,
}: TabCardsProps & TTabsClassNames) {
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleTabClick = (index: number) => {
    setCurrentSlide(index);
  };

  const currentTab = tabs[currentSlide];

  return (
    <div
      id="totalmente-integrado"
      className={`flex flex-col justify-center items-center my-40 pb-40 md:my-80 lg:mx-24 xl:mx-0 ${containerClassName}`}
    >
      <h2
        dangerouslySetInnerHTML={{ __html: title }}
        className="mx-24 md:mx-auto lg:mx-24 mb-16 md:mb-8 max-w-[455px] md:max-w-none font-display md:text-3xl lg:text-4xl text-center heading-3"
      />
      {description && (
        <p className="mb-16 md:mb-24 text-lg lg:text-xl text-center text-display-900">
          {description}
        </p>
      )}

      <div className="flex md:hidden flex-col">
        <div className="flex overflow-hidden mt-16 w-full max-w-[455px] h-full min-h-[240px] rounded-t-2xl">
          <Image
            src={currentTab.image.mobile.path}
            style={{ objectFit: 'cover' }}
            alt={currentTab.image.mobile.alternativeText}
            quality={currentTab.image.mobileQuality || 70}
            width={380}
            height={240}
            loading="lazy"
          />
        </div>
      </div>

      <div className="flex justify-center w-full">
        <div className="flex w-full md:bg-transparent">
          <div
            id={id}
            className="flex overflow-x-auto items-center md:px-24 mx-auto md:mb-24 w-max"
          >
            {tabs.map(({ label }, index) => (
              <div
                key={label}
                ref={el => {
                  tabRefs.current[index] = el;
                }}
                className="flex justify-center items-center last:pr-8 first:pl-8 h-56 md:h-[72px] bg-display-100 md:last:rounded-r-[40px] md:first:rounded-l-[40px] tabs-wrapper"
                data-tab-index={index}
              >
                <div
                  className={classNames(
                    'p-8 rounded-[40px] h-40 md:p-16 md:h-[56px] flex justify-center items-center',
                    {
                      'bg-display-0': currentSlide === index,
                    },
                  )}
                >
                  <button
                    type="button"
                    aria-label={label}
                    className="px-8"
                    onClick={() => handleTabClick(index)}
                    id={`fazer-negocio-girar-${index}`}
                  >
                    <h3
                      className={classNames(
                        'whitespace-nowrap h-40 text-[16px] font-semibold text-display-600',
                        {
                          'text-stone-500': currentSlide === index,
                        },
                      )}
                    >
                      {label}
                    </h3>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mt-24 md:mt-0 w-full max-w-full md:max-w-[1216px] h-[300px] md:h-[344px] lg:h-[422px] fader">
        {useMemo(
          () => (
            <div
              key={`card-${currentTab.title}`}
              className="flex absolute items-stretch md:px-24 lg:px-0 w-full h-full"
              style={{ opacity: 1 }}
            >
              <div className="flex overflow-hidden md:relative flex-col md:flex-row items-center md:p-24 lg:px-40 w-full md:rounded-2xl lg:rounded-6xl">
                <div className="hidden md:flex lg:hidden right-0 bottom-0 z-0">
                  <Image
                    src={currentTab.image.tablet.path}
                    style={{ objectFit: 'cover' }}
                    alt={currentTab.image.tablet.alternativeText}
                    quality={currentTab.image.tabletQuality || 70}
                    fill
                    loading="lazy"
                  />
                </div>

                <div className="hidden lg:flex md:absolute inset-0 z-0">
                  <Image
                    src={currentTab.image.web.path}
                    style={{ objectFit: 'cover' }}
                    alt={currentTab.image.web.alternativeText}
                    quality={currentTab.image.webQuality || 70}
                    fill
                    loading="lazy"
                  />
                </div>

                <div className="z-10 md:p-[23px] lg:p-40 px-24 md:mt-0 w-full max-w-[455px] md:max-w-[362px] lg:max-w-[455px] text-left md:bg-display-0 md:rounded-md lg:rounded-2xl">
                  <p
                    className="block mb-16 font-bold text-display-600 uppercase paragraph-14 lg:paragraph-16"
                    dangerouslySetInnerHTML={{ __html: currentTab.tag }}
                  />

                  <h4
                    className="mb-8 font-display text-2xl md:leading-[32px] text-display-900 heading-4 lg:heading-4"
                    dangerouslySetInnerHTML={{ __html: currentTab.title }}
                  />

                  <p
                    className="mb-16 text-display-900 paragraph-16 lg:paragraph-18"
                    dangerouslySetInnerHTML={{ __html: currentTab.description }}
                  />

                  <div className="w-full md:w-max">
                    {currentTab.buttons?.map(button => (
                      <Button
                        {...button}
                        key={button.label}
                        label={button.label || currentTab.label}
                        sectionReference={currentTab.title}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ),
          [currentSlide],
        )}
      </div>

      <div className="flex md:hidden gap-16 justify-center mb-40 w-full bg-transparent">
        <button
          type="button"
          className={`flex justify-center items-center w-40 h-40 bg-display-200 border-0 border-display-0 border-solid ${
            currentSlide > 0 &&
            'hover:bg-display-300 focus:border-2 active:border-2 focus:border-stone-200 active:border-stone-200'
          } rounded-xs`}
          onClick={() => handleTabClick(currentSlide - 1)}
          disabled={currentSlide === 0}
          aria-label="Voltar para aba anterior"
        >
          <Icon
            name="chevron-left"
            className={`w-20 h-20 font-bold fill-current ${
              currentSlide === 0
                ? 'text-display-400'
                : 'text-display-700 hover:text-display-800'
            }`}
          />
        </button>

        <button
          type="button"
          className={`flex justify-center items-center w-40 h-40 bg-display-200 border-0 border-display-0 border-solid ${
            !(currentSlide === tabs.length - 1) &&
            'hover:bg-display-300 focus:border-2 active:border-2 focus:border-stone-200 active:border-stone-200'
          } rounded-xs`}
          onClick={() => handleTabClick(currentSlide + 1)}
          disabled={currentSlide === tabs.length - 1}
          aria-label="Ir para próxima aba"
        >
          <Icon
            name="chevron-right"
            className={`w-20 h-20 font-bold fill-current ${
              currentSlide === tabs.length - 1
                ? 'text-display-400'
                : 'text-display-700 hover:text-display-800'
            }`}
          />
        </button>
      </div>
    </div>
  );
}
